/**
 * محرّكُ المساعد (29 سبتمبر 2026) — يفهم سؤالاً بالعامية أو الفصحى أو بأخطاءٍ إملائية ويعيد
 * **أقربَ** مدخلٍ في الدليل، بلا نموذجٍ لغوي ولا شبكة: كلُّ شيءٍ يجري في متصفّح السائل، فلا يغادر
 * السؤالُ الجهاز (قرار 17 سبتمبر 2026 — لا شيءَ يخرج من الأردن).
 *
 * الطبقات:
 *   ١) **التطبيع**: حذفُ التشكيل والتطويل، توحيدُ الهمزات والتاء المربوطة والألف المقصورة، الأرقامُ
 *      العربية إلى لاتينية، حذفُ علامات الترقيم والحشو (`STOPWORDS`).
 *   ٢) **المعجم**: كلُّ مرادفٍ ولهجةٍ في `SYNONYMS` يُردّ إلى مفهومه — «ابي» و«بدي» و«عايز» كلُّها «اريد».
 *      العباراتُ المركّبة («ولي امر»، «ما وصلني») تُطابَق قبل الكلمات المفردة.
 *   ٣) **التجذير الخفيف**: قصُّ السوابق واللواحق الشائعة بحدٍّ أدنى لطول الجذر.
 *   ٤) **الوزن**: كلُّ مدخلٍ مفهرسٌ بحقوله (عنوان · كلمات مفتاحية · أسئلة شائعة · ماذا تفعل · خطوات)،
 *      لكلِّ حقلٍ وزن. المطابقةُ التامة أعلى، وإن لم تطابق كلمةٌ شيئاً تُقارَن **بالتشابه** — مسافةُ
 *      تحريرٍ محدودة بطول الكلمة، ثم مقاطعُ ثلاثية على النصّ كلِّه — فلا يضيع سؤالٌ لخطأٍ إملائي.
 *   ٥) **الجواب**: إن بلغت أعلى نتيجةٍ عتبةَ الثقة فهي جوابٌ؛ وإلا تُعرض أقربُ ثلاثةٍ بعنوان «أقرب ما
 *      وجدت» — لا يُخترع جوابٌ ولا يُقال «لا أعرف» وحدها.
 *
 * `test/assistant.test.ts` يثبت أسئلةً حقيقية بلهجاتٍ مختلفة تصل إلى مدخلها.
 */
import { LIGHT_TERMS, PREFIXES, STOPWORDS, SUFFIXES, SYNONYMS } from './assistant-lexicon';

export interface AssistantEntry {
  /** المسارُ (أو مفتاحٌ فريد لسؤالٍ شائع بلا شاشة) */
  key: string;
  to: string | null;
  title: string;
  /** جملةٌ واحدة تُعرض جواباً */
  what: string;
  steps: string[];
  tips?: string[];
  /** كلماتٌ ومرادفاتٌ تُوزن كالعنوان */
  keywords?: string;
  /** صياغاتُ أسئلةٍ حقيقية تصل إلى هذا المدخل */
  questions?: string[];
}

export interface AssistantHit { entry: AssistantEntry; score: number }
export interface AssistantResult {
  /** `answer` ثقةٌ كافية · `nearest` أقربُ ما وجد · `empty` لا سؤال */
  kind: 'answer' | 'nearest' | 'empty';
  hits: AssistantHit[];
  /** الكلماتُ التي فُهمت من السؤال بعد التطبيع والمعجم — للاختبار وللعرض */
  understood: string[];
}

/* ── ١) التطبيع ─────────────────────────────────────────────────────────── */
const AR_DIGITS = '٠١٢٣٤٥٦٧٨٩';
export function normalizeText(s: string): string {
  return s
    .toLowerCase()
    .replace(/[ً-ْٰـ]/g, '')       // تشكيل وتطويل
    .replace(/[أإآٱ]/g, 'ا')
    .replace(/ة/g, 'ه')
    .replace(/ى/g, 'ي')
    .replace(/ؤ/g, 'و')
    .replace(/ئ/g, 'ي')
    .replace(/[٠-٩]/g, (d) => String(AR_DIGITS.indexOf(d)))
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/* ── ٢) المعجم: عباراتٌ ثم كلمات ─────────────────────────────────────────── */
interface Lexicon { phrases: Array<[string[], string]>; words: Map<string, string> }
let lexicon: Lexicon | null = null;
function buildLexicon(): Lexicon {
  if (lexicon) return lexicon;
  const phrases: Array<[string[], string]> = [];
  const words = new Map<string, string>();
  for (const [concept, list] of Object.entries(SYNONYMS)) {
    for (const raw of list) {
      const n = normalizeText(raw);
      if (!n) continue;
      const parts = n.split(' ');
      if (parts.length > 1) phrases.push([parts, concept]);
      else words.set(n, concept);
    }
    // المفهومُ نفسُه يُكتب أحياناً (اريد، احالة)
    const self = normalizeText(concept.replace(/_/g, ' '));
    if (self.includes(' ')) phrases.push([self.split(' '), concept]); else words.set(self, concept);
  }
  // الأطولُ أولاً فلا تبتلع «ولي» عبارةَ «ولي الامر»
  phrases.sort((a, b) => b[0].length - a[0].length);
  lexicon = { phrases, words };
  return lexicon;
}

/* ── ٣) التجذير الخفيف ───────────────────────────────────────────────────── */
export function stem(w: string): string {
  if (w.length <= 3) return w;
  let s = w;
  for (const p of PREFIXES) { if (s.startsWith(p) && s.length - p.length >= 3) { s = s.slice(p.length); break; } }
  for (const x of SUFFIXES) { if (s.endsWith(x) && s.length - x.length >= 3) { s = s.slice(0, -x.length); break; } }
  return s;
}

/** جملةٌ ← قائمةُ مفاهيمَ وجذورٍ (بلا حشو). المفاهيمُ من المعجم تُعاد بصورتها، وغيرُها مجذّراً */
export function tokenize(s: string): string[] {
  const lex = buildLexicon();
  const words = normalizeText(s).split(' ').filter(Boolean);
  const out: string[] = [];
  let i = 0;
  while (i < words.length) {
    let matched = false;
    for (const [parts, concept] of lex.phrases) {
      if (parts.length <= words.length - i && parts.every((p, k) => words[i + k] === p)) {
        out.push(concept); i += parts.length; matched = true; break;
      }
    }
    if (matched) continue;
    const w = words[i++];
    if (STOPWORDS.has(w)) continue;
    const c = lex.words.get(w) ?? lex.words.get(stem(w));
    if (c) { out.push(c); continue; }
    const st = stem(w);
    if (STOPWORDS.has(st)) continue;
    if (st.length >= 2) out.push(st);
  }
  return out;
}

/* ── ٤) الفهرسة والوزن ───────────────────────────────────────────────────── */
/** أفعالُ السؤال: تحمل نيّةً لكنها تتكرّر في كل سؤال فوزنُها خفيف (القائمةُ في المعجم) */
const LIGHT = LIGHT_TERMS;
const FIELD_WEIGHT = { title: 4, keywords: 3, questions: 3, what: 1.5, steps: 1, tips: 0.7 } as const;

interface Indexed { entry: AssistantEntry; terms: Map<string, number>; text: string; questionTexts: string[]; questionTokens: Set<string>[] }
function index(entries: AssistantEntry[]): Indexed[] {
  return entries.map((entry) => {
    const terms = new Map<string, number>();
    const add = (text: string | undefined, w: number) => {
      if (!text) return;
      for (const t of tokenize(text)) terms.set(t, Math.max(terms.get(t) ?? 0, w));
    };
    add(entry.title, FIELD_WEIGHT.title);
    add(entry.keywords, FIELD_WEIGHT.keywords);
    for (const q of entry.questions ?? []) add(q, FIELD_WEIGHT.questions);
    add(entry.what, FIELD_WEIGHT.what);
    for (const s of entry.steps) add(s, FIELD_WEIGHT.steps);
    for (const s of entry.tips ?? []) add(s, FIELD_WEIGHT.tips);
    const text = normalizeText([entry.title, entry.keywords ?? '', entry.what].join(' '));
    const questionTexts = (entry.questions ?? []).map(normalizeText);
    return { entry, terms, text, questionTexts, questionTokens: (entry.questions ?? []).map((q) => new Set(tokenize(q).filter((t) => !LIGHT.has(t)))) };
  });
}

/** مسافةُ التحرير (دامرو–ليفنشتاين بلا تبديلٍ متجاور — يكفي للأخطاء الإملائية) بسقفٍ يوقف الحساب مبكراً */
export function editDistance(a: string, b: string, max: number): number {
  if (Math.abs(a.length - b.length) > max) return max + 1;
  const prev = new Array<number>(b.length + 1); const cur = new Array<number>(b.length + 1);
  for (let j = 0; j <= b.length; j++) prev[j] = j;
  for (let i = 1; i <= a.length; i++) {
    cur[0] = i; let rowMin = i;
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost);
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) cur[j] = Math.min(cur[j], prev[j - 1] + 1);
      rowMin = Math.min(rowMin, cur[j]);
    }
    if (rowMin > max) return max + 1;
    for (let j = 0; j <= b.length; j++) prev[j] = cur[j];
  }
  return prev[b.length];
}
const allowedEdits = (len: number) => (len <= 3 ? 0 : len <= 5 ? 1 : 2);

/** تشابهُ المقاطع الثلاثية (معامل دايس) — للنصّ كلِّه، يلتقط ما فاتت الكلماتُ المفردة */
export function trigramSimilarity(a: string, b: string): number {
  const grams = (s: string) => { const g = new Map<string, number>(); const p = ` ${s} `; for (let i = 0; i + 3 <= p.length; i++) { const k = p.slice(i, i + 3); g.set(k, (g.get(k) ?? 0) + 1); } return g; };
  const ga = grams(a); const gb = grams(b);
  if (!ga.size || !gb.size) return 0;
  let inter = 0; for (const [k, n] of ga) inter += Math.min(n, gb.get(k) ?? 0);
  let na = 0; for (const n of ga.values()) na += n; let nb = 0; for (const n of gb.values()) nb += n;
  return (2 * inter) / (na + nb);
}

/** عتبةُ الثقة: نتيجةٌ ≥ هذه (بعد التطبيع بعدد كلمات السؤال المهمّة) تُعدّ جواباً لا تقريباً */
export const ANSWER_THRESHOLD = 2.4;

export function ask(question: string, entries: AssistantEntry[], limit = 3): AssistantResult {
  const tokens = tokenize(question);
  const heavy = tokens.filter((t) => !LIGHT.has(t));
  if (tokens.length === 0) return { kind: 'empty', hits: [], understood: [] };
  const idx = index(entries);
  const qNorm = normalizeText(question);
  const scored: AssistantHit[] = idx.map((d) => {
    let score = 0; let exactHeavy = 0;
    for (const t of tokens) {
      const light = LIGHT.has(t);
      const w = d.terms.get(t);
      if (w !== undefined) { score += light ? Math.min(w, 0.4) : w; if (!light) exactHeavy++; continue; }
      if (light || t.length < 4) continue;
      // تشابهُ كلمة: أقربُ مصطلحٍ في المدخل بمسافةٍ محدودة أو بالاحتواء (جذرٌ داخل كلمة)
      const max = allowedEdits(t.length);
      let best = 0;
      for (const [term, w2] of d.terms) {
        if (term.length < 4) continue;
        if (term.includes(t) || t.includes(term)) { best = Math.max(best, w2 * 0.7); continue; }
        if (max > 0 && editDistance(t, term, max) <= max) best = Math.max(best, w2 * 0.55);
      }
      score += best;
    }
    // تشابهُ الجملة كلِّها مع الأسئلة الشائعة والعنوان — يلتقط الصياغات القريبة
    let sim = trigramSimilarity(qNorm, d.text);
    for (const qt of d.questionTexts) sim = Math.max(sim, trigramSimilarity(qNorm, qt) * 1.3);
    score += sim * 3;
    // سؤالٌ شائعٌ بالمعنى نفسِه: مفاهيمُ السؤال المهمّة هي مفاهيمُ سؤالٍ مسجَّل (بأي صياغة) ← مكافأةٌ كبيرة
    if (heavy.length > 0) {
      const hs = new Set(heavy);
      for (const qt of d.questionTokens) {
        if (qt.size === 0) continue;
        let common = 0; for (const t of hs) if (qt.has(t)) common++;
        const jaccard = common / (hs.size + qt.size - common);
        if (jaccard >= 0.99) { score += 4; break; }
        if (jaccard >= 0.6) score = Math.max(score, score + 2 * jaccard);
      }
    }
    // مكافأةُ تغطية: كلُّ كلمات السؤال المهمّة وُجدت تماماً
    if (heavy.length > 0 && exactHeavy === heavy.length) score *= 1.25;
    return { entry: d.entry, score };
  });
  scored.sort((a, b) => b.score - a.score);
  const hits = scored.filter((h) => h.score > 0.6).slice(0, limit);
  if (hits.length === 0) return { kind: 'nearest', hits: scored.slice(0, limit), understood: tokens };
  const top = hits[0].score / Math.max(1, Math.min(heavy.length, 3));
  return { kind: top >= ANSWER_THRESHOLD ? 'answer' : 'nearest', hits, understood: tokens };
}
